import React from 'react';
import styled from 'styled-components';
import SEO from '../components/SEO';

const NavbarSpacer = styled.div`
  height: 120px;
  @media (min-width: 768px) {
    height: 140px;
  }
  @media (min-width: 1024px) {
    height: 160px;
  }
`;

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #fff;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 16px;
  
  @media (min-width: 768px) {
    padding: 60px 24px;
  }
  
  @media (min-width: 1024px) {
    padding: 80px 24px;
  }
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 20px;
  
  @media (min-width: 768px) {
    font-size: 48px;
  }
`;

const LastUpdated = styled.p`
  color: #666;
  font-size: 14px;
  margin-bottom: 40px;
`;

const Section = styled.section`
  margin-bottom: 40px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 16px;
  margin-top: 32px;
  
  @media (min-width: 768px) {
    font-size: 28px;
  }
`;

const Paragraph = styled.p`
  font-size: 16px;
  line-height: 1.8;
  color: #333;
  margin-bottom: 16px;
`;

const List = styled.ul`
  font-size: 16px;
  line-height: 1.8;
  color: #333;
  margin-bottom: 16px;
  padding-left: 24px;
`;

const ListItem = styled.li`
  margin-bottom: 8px;
`;

const ContactInfo = styled.div`
  background: #f5f5f5;
  padding: 24px;
  border-radius: 8px;
  margin-top: 32px;
`;

const ContactTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 12px;
`;

const ContactText = styled.p`
  font-size: 16px;
  line-height: 1.8;
  color: #333;
  margin-bottom: 8px;
`;

const TermsConditionsPage = () => {
  return (
    <PageContainer>
      <SEO 
        title="Terms & Conditions"
        description="Read TheMadZone's Terms & Conditions. Understand our terms of service, user agreements, and policies for using our website and purchasing products."
        keywords="terms and conditions, terms of service, user agreement, TheMadZone terms"
      />
      <NavbarSpacer />
      <ContentWrapper>
        <Title>Terms & Conditions</Title>
        <LastUpdated>Last Updated: {new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}</LastUpdated>

        <Section>
          <Paragraph>
            Welcome to TheMadZone. These Terms and Conditions ("Terms") govern your use of our website and your purchase of products from us. By accessing our website or making a purchase, you agree to be bound by these Terms.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>1. Acceptance of Terms</SectionTitle>
          
          <Paragraph>
            By accessing and using TheMadZone's website, you accept and agree to be bound by these Terms and Conditions. If you do not agree to these Terms, please do not use our website or make purchases from us.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>2. Use of Website</SectionTitle>
          
          <Paragraph>You agree to use our website only for lawful purposes and in accordance with these Terms. You agree not to:</Paragraph>
          <List>
            <ListItem>Use the website in any way that violates applicable laws or regulations</ListItem>
            <ListItem>Attempt to gain unauthorized access to any part of the website</ListItem>
            <ListItem>Interfere with or disrupt the website or servers</ListItem>
            <ListItem>Use automated systems to access the website without permission</ListItem>
            <ListItem>Reproduce, duplicate, copy, or resell any part of the website</ListItem>
            <ListItem>Use the website to transmit any harmful or malicious code</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>3. Account Registration</SectionTitle>
          
          <Paragraph>
            To make purchases, you may need to create an account. You are responsible for:
          </Paragraph>
          <List>
            <ListItem>Maintaining the confidentiality of your account credentials</ListItem>
            <ListItem>All activities that occur under your account</ListItem>
            <ListItem>Providing accurate and complete information</ListItem>
            <ListItem>Notifying us immediately of any unauthorized use</ListItem>
          </List>
          
          <Paragraph>
            We reserve the right to suspend or terminate accounts that violate these Terms or engage in fraudulent activity.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>4. Products and Pricing</SectionTitle>
          
          <Paragraph>
            <strong>Product Information:</strong> We strive to provide accurate product descriptions, images, and pricing. However, we do not warrant that product descriptions or other content on the website is accurate, complete, or error-free.
          </Paragraph>
          
          <Paragraph>
            <strong>Pricing:</strong> All prices are displayed in British Pounds (GBP) and include VAT where applicable. We reserve the right to change prices at any time, but changes will not affect orders already placed.
          </Paragraph>
          
          <Paragraph>
            <strong>Availability:</strong> Product availability is subject to change. If a product becomes unavailable after you place an order, we will notify you and provide a refund or alternative option.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>5. Orders and Payment</SectionTitle>
          
          <Paragraph>
            <strong>Order Acceptance:</strong> Your order constitutes an offer to purchase. We reserve the right to accept or reject any order at our discretion. An order confirmation email does not constitute acceptance of your order.
          </Paragraph>
          
          <Paragraph>
            <strong>Payment:</strong> Payment must be made at the time of purchase using accepted payment methods. We use secure payment processors to handle transactions. By providing payment information, you represent that you are authorized to use the payment method.
          </Paragraph>
          
          <Paragraph>
            <strong>Order Cancellation:</strong> You may cancel your order before it ships. Once an order has shipped, our Return Policy applies.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>6. Shipping and Delivery</SectionTitle>
          
          <Paragraph>
            <strong>Shipping Areas:</strong> We currently ship only to the United Kingdom (England, Wales, Scotland, Northern Ireland, Isle of Man, and Isle of Skye).
          </Paragraph>
          
          <Paragraph>
            <strong>Delivery Times:</strong> Estimated delivery times are provided at checkout and are estimates only. We are not liable for delays caused by shipping carriers or circumstances beyond our control.
          </Paragraph>
          
          <Paragraph>
            <strong>Delivery Address:</strong> You are responsible for providing an accurate delivery address. We are not responsible for orders delivered to incorrect addresses provided by you.
          </Paragraph>
          
          <Paragraph>
            <strong>Risk of Loss:</strong> Risk of loss and title for products pass to you upon delivery to the carrier.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>7. Returns and Refunds</SectionTitle>
          
          <Paragraph>
            Please refer to our <strong>Return Policy</strong> for detailed information about returns, exchanges, and refunds. Our Return Policy is incorporated into these Terms by reference.
          </Paragraph>
          
          <Paragraph>
            <strong>Gift Card Refund Policy:</strong> All gift card purchases are final and non-refundable once the transaction is successful. Gift cards cannot be returned, exchanged, or refunded under any circumstances. By purchasing a gift card, you acknowledge and agree that the sale is final.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>8. Intellectual Property</SectionTitle>
          
          <Paragraph>
            All content on TheMadZone's website, including text, graphics, logos, images, and software, is the property of TheMadZone or its licensors and is protected by copyright, trademark, and other intellectual property laws.
          </Paragraph>
          
          <Paragraph>
            You may not reproduce, distribute, modify, or create derivative works from any content on our website without our express written permission.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>9. Limitation of Liability</SectionTitle>
          
          <Paragraph>
            To the maximum extent permitted by law, TheMadZone shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses.
          </Paragraph>
          
          <Paragraph>
            Our total liability to you for all claims arising from or related to your use of the website or products shall not exceed the amount you paid to us in the 12 months preceding the claim.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>10. Indemnification</SectionTitle>
          
          <Paragraph>
            You agree to indemnify and hold harmless TheMadZone, its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from your use of the website, violation of these Terms, or infringement of any rights of another.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>11. Governing Law</SectionTitle>
          
          <Paragraph>
            These Terms shall be governed by and construed in accordance with the laws of England and Wales. Any disputes arising from these Terms or your use of the website shall be subject to the exclusive jurisdiction of the courts of England and Wales.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>12. Changes to Terms</SectionTitle>
          
          <Paragraph>
            We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting on the website. Your continued use of the website after changes are posted constitutes acceptance of the modified Terms.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>13. Severability</SectionTitle>
          
          <Paragraph>
            If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall continue in full force and effect.
          </Paragraph>
        </Section>

        <ContactInfo>
          <ContactTitle>Contact Us</ContactTitle>
          <ContactText>
            If you have any questions about these Terms & Conditions, please contact us:
          </ContactText>
          <ContactText>
            <strong>Email:</strong> hello@themadzone.com
          </ContactText>
          <ContactText>
            <strong>Address:</strong><br />
            Apartment 2, 45-47 Savile Court<br />
            Hull, England, HU1 3BT<br />
            United Kingdom
          </ContactText>
          <ContactText>
            <strong>Company:</strong> TheMadZone
          </ContactText>
          <ContactText>
            <strong>Company Number:</strong> THEMADZONE LTD - Company number 16470350
          </ContactText>
        </ContactInfo>
      </ContentWrapper>
    </PageContainer>
  );
};

export default TermsConditionsPage;

